import { useRef, useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FolderPlus, Folder, ChevronRight, Eye, EyeOff, Trash2, FileText, Image as ImageIcon, Mic, Video, Upload, Paperclip, FolderOpen, Pencil, Check, X, Move } from "lucide-react";
import {
  listarAnexosProjeto,
  uploadAnexoProjeto,
  atualizarAnexoProjeto,
  deletarAnexoProjeto,
  listarPastasProjeto,
  criarPastaProjeto,
  atualizarPasta,
  deletarPasta,
  ProjetoAnexo,
  ProjetoPasta,
} from "../../services/projetoAnexos";
import { anexoUrlAbsoluto } from "../../services/tarefas";
import { useAuth } from "../../hooks/useAuth";
import { useConfirm } from "../../components/ConfirmProvider";
import { AudioWavePlayer } from "../../components/AudioWavePlayer";

export function ProjetoAnexosSection({ projetoId }: { projetoId: string }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [marcarVisivel, setMarcarVisivel] = useState(true);
  const [pastaAtualId, setPastaAtualId] = useState<string | null>(null);
  const [criandoPasta, setCriandoPasta] = useState(false);
  const [novoNomePasta, setNovoNomePasta] = useState("");
  const [renomeandoId, setRenomeandoId] = useState<string | null>(null);
  const [novoNome, setNovoNome] = useState("");
  const [movendoAnexo, setMovendoAnexo] = useState<ProjetoAnexo | null>(null);

  const { data: anexos = [] } = useQuery({
    queryKey: ["projeto-anexos", projetoId],
    queryFn: () => listarAnexosProjeto(projetoId),
  });
  const { data: pastas = [] } = useQuery({
    queryKey: ["projeto-pastas", projetoId],
    queryFn: () => listarPastasProjeto(projetoId),
  });

  const podeOperar = can(["gestor", "admin"]);

  const invalidar = () => {
    qc.invalidateQueries({ queryKey: ["projeto-anexos", projetoId] });
    qc.invalidateQueries({ queryKey: ["projeto-pastas", projetoId] });
  };

  const uploadM = useMutation({
    mutationFn: (file: File) => uploadAnexoProjeto(projetoId, file, { visivelParaCliente: marcarVisivel, pastaId: pastaAtualId }),
    onSuccess: invalidar,
  });
  const updateAnexoM = useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: any }) => atualizarAnexoProjeto(projetoId, id, dados),
    onSuccess: invalidar,
  });
  const deleteAnexoM = useMutation({
    mutationFn: (id: string) => deletarAnexoProjeto(projetoId, id),
    onSuccess: invalidar,
  });
  const criarPastaM = useMutation({
    mutationFn: () => criarPastaProjeto(projetoId, { nome: novoNomePasta.trim(), paiId: pastaAtualId }),
    onSuccess: () => { invalidar(); setCriandoPasta(false); setNovoNomePasta(""); },
  });
  const renomearPastaM = useMutation({
    mutationFn: ({ id, nome }: { id: string; nome: string }) => atualizarPasta(id, { nome }),
    onSuccess: () => { invalidar(); setRenomeandoId(null); },
  });
  const moverPastaM = useMutation({
    mutationFn: ({ id, paiId }: { id: string; paiId: string | null }) => atualizarPasta(id, { paiId }),
    onSuccess: invalidar,
  });
  const deletePastaM = useMutation({
    mutationFn: deletarPasta,
    onSuccess: invalidar,
  });

  // Subpastas e arquivos da pasta atual
  const pastasDaAtual = useMemo(
    () => pastas.filter((p) => p.paiId === pastaAtualId).sort((a, b) => a.ordem - b.ordem || a.nome.localeCompare(b.nome)),
    [pastas, pastaAtualId],
  );
  const arquivosDaAtual = useMemo(
    () => anexos.filter((a) => a.pastaId === pastaAtualId),
    [anexos, pastaAtualId],
  );

  // Breadcrumb: caminho até a raiz
  const caminho = useMemo(() => {
    const r: ProjetoPasta[] = [];
    let cursor = pastaAtualId;
    while (cursor) {
      const p = pastas.find((x) => x.id === cursor);
      if (!p) break;
      r.unshift(p);
      cursor = p.paiId;
    }
    return r;
  }, [pastas, pastaAtualId]);

  const [dragOver, setDragOver] = useState(false);
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (!podeOperar) return;
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach((f) => uploadM.mutate(f));
  }

  return (
    <div
      className={`bg-white rounded-xl border p-6 transition-colors ${dragOver ? "border-blue-400 ring-2 ring-blue-200 bg-blue-50/40" : "border-slate-200"}`}
      onDragOver={(e) => { if (podeOperar) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <h3 className="text-sm font-medium text-slate-700 flex items-center gap-2">
          <Paperclip size={16} /> Arquivos do projeto
          {podeOperar && <span className="text-[10px] text-slate-400 font-normal">· arraste pra fazer upload na pasta atual</span>}
        </h3>
        {podeOperar && (
          <div className="flex items-center gap-2 flex-wrap">
            <label className="flex items-center gap-1 text-xs text-slate-600">
              <input type="checkbox" checked={marcarVisivel} onChange={(e) => setMarcarVisivel(e.target.checked)} />
              Visível ao cliente
            </label>
            <button
              onClick={() => setCriandoPasta(true)}
              className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs px-3 py-1.5 rounded"
              title="Nova pasta"
            >
              <FolderPlus size={12} /> Nova pasta
            </button>
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded"
            >
              <Upload size={12} /> Enviar arquivo
            </button>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadM.mutate(f);
                if (fileRef.current) fileRef.current.value = "";
              }}
            />
          </div>
        )}
      </div>

      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-sm mb-3 flex-wrap">
        <button
          onClick={() => setPastaAtualId(null)}
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded hover:bg-slate-100 ${pastaAtualId === null ? "font-semibold text-slate-800" : "text-slate-500"}`}
        >
          <FolderOpen size={12} /> Raiz
        </button>
        {caminho.map((p) => (
          <span key={p.id} className="inline-flex items-center gap-1">
            <ChevronRight size={12} className="text-slate-300" />
            <button
              onClick={() => setPastaAtualId(p.id)}
              className={`px-2 py-0.5 rounded hover:bg-slate-100 ${p.id === pastaAtualId ? "font-semibold text-slate-800" : "text-slate-500"}`}
            >
              {p.nome}
            </button>
          </span>
        ))}
      </div>

      {/* Form de criar pasta */}
      {criandoPasta && podeOperar && (
        <div className="flex items-center gap-2 mb-3 p-2 rounded border border-blue-200 bg-blue-50">
          <Folder size={14} className="text-blue-600" />
          <input
            type="text"
            value={novoNomePasta}
            onChange={(e) => setNovoNomePasta(e.target.value)}
            placeholder="Nome da pasta"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && novoNomePasta.trim()) criarPastaM.mutate();
              if (e.key === "Escape") { setCriandoPasta(false); setNovoNomePasta(""); }
            }}
            className="flex-1 px-2 py-1 rounded border border-slate-300 text-sm outline-none focus:border-blue-500"
          />
          <button
            onClick={() => criarPastaM.mutate()}
            disabled={!novoNomePasta.trim() || criarPastaM.isPending}
            className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded disabled:opacity-50"
          >
            Criar
          </button>
          <button
            onClick={() => { setCriandoPasta(false); setNovoNomePasta(""); }}
            className="text-xs text-slate-500"
          >
            Cancelar
          </button>
        </div>
      )}

      {/* Conteúdo: pastas + arquivos */}
      {pastasDaAtual.length === 0 && arquivosDaAtual.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6">Pasta vazia.</p>
      )}

      <div className="space-y-2">
        {pastasDaAtual.map((p) => (
          <PastaLinha
            key={p.id}
            pasta={p}
            podeOperar={podeOperar}
            renomeando={renomeandoId === p.id}
            novoNome={novoNome}
            onSetNovoNome={setNovoNome}
            onAbrir={() => setPastaAtualId(p.id)}
            onIniciarRenomear={() => { setRenomeandoId(p.id); setNovoNome(p.nome); }}
            onSalvarRenomear={() => renomearPastaM.mutate({ id: p.id, nome: novoNome.trim() })}
            onCancelarRenomear={() => setRenomeandoId(null)}
            onDeletar={async () => {
              const ok = await confirm({
                title: `Apagar pasta "${p.nome}"?`,
                message: "Subpastas serão apagadas também. Arquivos ficam na raiz do projeto.",
                danger: true,
                confirmText: "Apagar",
              });
              if (ok) deletePastaM.mutate(p.id);
            }}
          />
        ))}
        {arquivosDaAtual.map((a) => (
          <AnexoLinha
            key={a.id}
            a={a}
            podeOperar={podeOperar}
            onToggleVis={() => updateAnexoM.mutate({ id: a.id, dados: { visivelParaCliente: !a.visivelParaCliente } })}
            onDelete={async () => {
              const ok = await confirm({ title: "Remover arquivo", message: `Remover "${a.nomeOriginal}"?`, danger: true, confirmText: "Remover" });
              if (ok) deleteAnexoM.mutate(a.id);
            }}
            onMover={() => setMovendoAnexo(a)}
          />
        ))}
      </div>

      {uploadM.isPending && <p className="text-xs text-slate-500 mt-2">Enviando…</p>}

      {/* Modal: mover anexo entre pastas */}
      {movendoAnexo && (
        <MoverModal
          arquivo={movendoAnexo}
          pastas={pastas}
          onClose={() => setMovendoAnexo(null)}
          onConfirmar={(novaPastaId) => {
            updateAnexoM.mutate({ id: movendoAnexo.id, dados: { pastaId: novaPastaId } });
            setMovendoAnexo(null);
          }}
        />
      )}
      {/* Modal: mover pasta entre pastas (só admin/gestor) */}
      {/* Reusa MoverModal mas aplicado a pasta */}
      <MoverPastaHelper
        pastas={pastas}
        onMover={(id, paiId) => moverPastaM.mutate({ id, paiId })}
      />
    </div>
  );
}

function PastaLinha({ pasta, podeOperar, renomeando, novoNome, onSetNovoNome, onAbrir, onIniciarRenomear, onSalvarRenomear, onCancelarRenomear, onDeletar }: {
  pasta: ProjetoPasta;
  podeOperar: boolean;
  renomeando: boolean;
  novoNome: string;
  onSetNovoNome: (v: string) => void;
  onAbrir: () => void;
  onIniciarRenomear: () => void;
  onSalvarRenomear: () => void;
  onCancelarRenomear: () => void;
  onDeletar: () => void;
}) {
  return (
    <div className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 group flex items-center gap-2">
      <Folder size={16} className="text-amber-500 shrink-0" />
      {renomeando ? (
        <>
          <input
            type="text"
            value={novoNome}
            onChange={(e) => onSetNovoNome(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && novoNome.trim()) onSalvarRenomear();
              if (e.key === "Escape") onCancelarRenomear();
            }}
            className="flex-1 px-2 py-0.5 rounded border border-blue-300 text-sm outline-none"
          />
          <button onClick={onSalvarRenomear} className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"><Check size={14} /></button>
          <button onClick={onCancelarRenomear} className="p-1 text-slate-500 hover:bg-slate-100 rounded"><X size={14} /></button>
        </>
      ) : (
        <>
          <button onClick={onAbrir} className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-blue-600 truncate">
            {pasta.nome}
          </button>
          {podeOperar && (
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-0.5">
              <button onClick={onIniciarRenomear} className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded" title="Renomear">
                <Pencil size={12} />
              </button>
              <button onClick={onDeletar} className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" title="Apagar">
                <Trash2 size={12} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AnexoLinha({ a, podeOperar, onToggleVis, onDelete, onMover }: {
  a: ProjetoAnexo;
  podeOperar: boolean;
  onToggleVis: () => void;
  onDelete: () => void;
  onMover: () => void;
}) {
  const url = anexoUrlAbsoluto(a.url);
  const Icon = a.tipo === "imagem" ? ImageIcon : a.tipo === "audio" ? Mic : a.tipo === "video" ? Video : FileText;

  return (
    // Sem opacidade: com `/50` a regra do dark mode (sobrescrita global de
    // classe no index.css) não casa e o cartão fica claro no tema escuro —
    // mesmo bug do AnexoView, jul/2026.
    <div className="border border-slate-200 rounded-lg p-3 bg-slate-50 group">
      <div className="flex items-center gap-2">
        <Icon size={14} className="text-slate-400 shrink-0" />
        <a href={url} target="_blank" rel="noreferrer" className="flex-1 truncate text-sm text-slate-700 hover:text-blue-600">
          {a.nomeOriginal}
        </a>
        <span className="text-[10px] text-slate-400">{(a.tamanhoBytes / 1024).toFixed(0)} KB</span>
        {podeOperar && (
          <>
            <button
              onClick={onToggleVis}
              className={`flex items-center gap-1 text-[10px] px-2 py-0.5 rounded ${a.visivelParaCliente ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}
              title={a.visivelParaCliente ? "Visível ao cliente — clique para tornar interno" : "Interno — clique para mostrar ao cliente"}
            >
              {a.visivelParaCliente ? <Eye size={10} /> : <EyeOff size={10} />}
              {a.visivelParaCliente ? "PÚBLICO" : "INTERNO"}
            </button>
            <button onClick={onMover} className="text-slate-400 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" title="Mover para outra pasta">
              <Move size={12} />
            </button>
            <button onClick={onDelete} className="text-slate-400 hover:text-red-500" title="Remover">
              <Trash2 size={12} />
            </button>
          </>
        )}
      </div>
      {a.tipo === "imagem" && (
        <a href={url} target="_blank" rel="noreferrer">
          <img src={url} alt={a.nomeOriginal} className="mt-2 max-h-40 object-contain rounded border border-slate-100 w-full bg-white" />
        </a>
      )}
      {a.tipo === "audio" && <div className="mt-2"><AudioWavePlayer url={url} variante="claro" /></div>}
      {a.tipo === "video" && <video controls src={url} className="w-full max-h-56 mt-2" />}
    </div>
  );
}

function MoverModal({ arquivo, pastas, onClose, onConfirmar }: {
  arquivo: ProjetoAnexo;
  pastas: ProjetoPasta[];
  onClose: () => void;
  onConfirmar: (novaPastaId: string | null) => void;
}) {
  const [destino, setDestino] = useState<string | null>(arquivo.pastaId ?? null);
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-800">Mover arquivo</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700 truncate" title={arquivo.nomeOriginal}>📄 {arquivo.nomeOriginal}</p>
          <label className="text-xs text-slate-600 block">Mover para:</label>
          <select
            value={destino || ""}
            onChange={(e) => setDestino(e.target.value || null)}
            className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500"
          >
            <option value="">📁 Raiz do projeto</option>
            {pastas.map((p) => (
              <option key={p.id} value={p.id}>{caminhoDaPasta(p, pastas)}</option>
            ))}
          </select>
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded text-sm bg-slate-100 hover:bg-slate-200">Cancelar</button>
          <button onClick={() => onConfirmar(destino)} className="px-4 py-1.5 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white">
            Mover
          </button>
        </div>
      </div>
    </div>
  );
}

// Stub atualmente sem UI — fica disponível pra arrastar pastas no futuro.
function MoverPastaHelper(_props: { pastas: ProjetoPasta[]; onMover: (id: string, paiId: string | null) => void }) {
  return null;
}

function caminhoDaPasta(p: ProjetoPasta, todas: ProjetoPasta[]): string {
  const partes: string[] = [];
  let cursor: ProjetoPasta | undefined = p;
  while (cursor) {
    partes.unshift(cursor.nome);
    cursor = cursor.paiId ? todas.find((x) => x.id === cursor!.paiId) : undefined;
  }
  return "📁 " + partes.join(" / ");
}
